(() => {
  'use strict';

  if (window.__piedraEssentialAddonsLoaded) {
    window.__piedraEssentialAddonsRefresh?.();
    return;
  }

  window.__piedraEssentialAddonsLoaded = true;

  const SETTINGS = {
    engraving: {
      productKeywords: ['custom engraving', 'engraving'],
      cartAddonVariantIds: ['53567004344504'],
      cartAddonProductIds: ['10437662113976'],
      toggleLabelKeywords: ['custom engraving', 'engraving'],
      propertyName: 'Engraving text',
      maxCharacters: 35,
      existingPropertyKeywords: [
        'engraving',
        'engrave',
        'engraved',
        'engraving text',
        'custom text',
        'personalization',
        'personalisation',
        'personalized text',
        'personalised text'
      ],
      existingMessage: 'Already Selected'
    },

    color: {
      productKeywords: [
        'add color',
        'custom color',
        'color add-on',
        'color addon'
      ],
      cartAddonVariantIds: ['53835370168504'],
      cartAddonProductIds: ['10500784554168'],
      toggleLabelKeywords: ['add color', 'custom color', 'color add-on', 'color addon'],
      propertyName: 'Selected color',
      options: [
        { name: 'Noir Black', hex: '#171717' },
        { name: 'Coffee Brown', hex: '#6b4935' },
        { name: 'Wine Red', hex: '#742f3f' },
        { name: 'Ember Orange', hex: '#c65b2d' },
        { name: 'Deep Olive Green', hex: '#5B6B5C' }
      ],
      legacyNativeColorNames: ['Matcha Green'],
      nativeOptionNames: [
        'color',
        'colour',
        'finish',
        'colorway',
        'colourway'
      ],
      baseVariantValues: [
        'concrete grey',
        'concrete gray',
        'natural concrete',
        'raw concrete',
        'standard concrete'
      ],
      existingMessage: 'Already Selected'
    },

    target: {
      displayPropertyName: 'Applies to',
      keyPropertyName: '_Piedra target line',
      variantPropertyName: '_Piedra target variant',
      productPropertyName: '_Piedra target product',
      sourcePropertyName: '_Piedra source',
      sourceValue: 'cart add-on'
    },

    productOptionAppMarkers: [
      'easify',
      'easify options',
      'product options',
      'option set',
      'add-on product',
      'addon product',
      'linked product',
      'main product',
      'parent product'
    ],

    falseLikeValues: [
      '',
      'no',
      'none',
      'false',
      'off',
      'not selected',
      'no engraving',
      'without engraving',
      'standard',
      'default'
    ],

    saveDelay: 500,
    cartPollInterval: 900,
    toggleSearchDistance: 340
  };

  const state = {
    items: {
      engraving: null,
      color: null
    },
    existingSelections: {
      engraving: false,
      color: false
    },
    eligibleTargets: {
      engraving: [],
      color: []
    },
    selectedTargetKey: {
      engraving: '',
      color: ''
    },
    pendingTargetKey: {
      engraving: '',
      color: ''
    },
    lastSaved: {
      engraving: null,
      color: null
    },
    writeQueues: {
      engraving: Promise.resolve(),
      color: Promise.resolve()
    },
    engravingSaveTimer: null,
    pendingColor: null,
    refreshTimer: null,
    refreshInProgress: false,
    bypassCheckoutOnce: false,
    colorInstanceCount: 0,
    lockedControls: {
      engraving: null,
      color: null
    },
    noticeShown: {
      engraving: false,
      color: false
    },
    duplicateCleanupPromise: null
  };

  const nativeFetch = window.fetch.bind(window);
  const rootUrl = () => window.Shopify?.routes?.root || '/';

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isElementVisible(element) {
    if (!(element instanceof Element)) return false;

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function getVisibleOrFirst(selector) {
    const elements = Array.from(document.querySelectorAll(selector));

    return (
      elements.find((element) => element.offsetParent !== null) ||
      elements[0] ||
      null
    );
  }

  function getEngravingPlaceholder() {
    return getVisibleOrFirst('[data-piedra-engraving-placeholder]');
  }

  function getColorPlaceholder() {
    return getVisibleOrFirst('[data-piedra-color-placeholder]');
  }

  function setAnimatedVisibility(element, shouldShow) {
    if (!element) return;

    if (element.hidden) {
      element.hidden = false;
    }

    if (shouldShow) {
      element.setAttribute('aria-hidden', 'false');

      if (!element.classList.contains('is-visible')) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            element.classList.add('is-visible');
          });
        });
      }

      return;
    }

    element.classList.remove('is-visible');
    element.setAttribute('aria-hidden', 'true');
  }

  function setNoticeContent(notice, message) {
    if (!notice) return;
    notice.textContent = message;
  }

  /* ------------------ ASSOCIATED TOGGLE CONTROL ------------------ */

  const TOGGLE_SELECTOR = [
    'input[type="checkbox"]',
    'button[role="switch"]',
    '[role="switch"]',
    '[role="checkbox"]',
    '[aria-checked="true"]',
    '[aria-checked="false"]',
    'button[class*="switch" i]',
    'button[class*="toggle" i]',
    '[class*="switch" i][tabindex]',
    '[class*="toggle" i][tabindex]'
  ].join(', ');

  function findToggleInside(element) {
    if (!(element instanceof Element)) return null;

    const candidates = [];

    if (element.matches(TOGGLE_SELECTOR)) {
      candidates.push(element);
    }

    candidates.push(...element.querySelectorAll(TOGGLE_SELECTOR));

    return (
      candidates.filter(isElementVisible).at(-1) ||
      candidates.at(-1) ||
      null
    );
  }

  function elementTextWithoutPiedraInterface(element) {
    if (!(element instanceof Element)) return '';

    const clone = element.cloneNode(true);
    clone.querySelectorAll(
      '[data-piedra-engraving-placeholder], [data-piedra-color-placeholder]'
    ).forEach((node) => node.remove());

    return normalize(clone.textContent || '');
  }

  function findAssociatedUpsellRegionByLabel(kind, placeholder) {
    if (!placeholder) return null;

    const keywords = SETTINGS[kind]?.toggleLabelKeywords || [];
    if (!keywords.length) return null;

    const hasLabel = (element) => {
      const text = elementTextWithoutPiedraInterface(element);
      return keywords.some((keyword) => text.includes(normalize(keyword)));
    };

    /* First choice: a preceding sibling branch that contains the upsell label. */
    let branch = placeholder;

    for (let depth = 0; depth < 7 && branch; depth += 1) {
      let sibling = branch.previousElementSibling;
      let inspected = 0;

      while (sibling && inspected < 8) {
        if (isElementVisible(sibling) && hasLabel(sibling)) {
          const rect = sibling.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0 && rect.height <= 240) {
            return sibling;
          }
        }

        sibling = sibling.previousElementSibling;
        inspected += 1;
      }

      branch = branch.parentElement;
    }

    /* Mobile fallback: find the smallest visible labeled box nearest the HTML block. */
    const placeholderRect = placeholder.getBoundingClientRect();
    const candidates = Array.from(document.querySelectorAll('div, li, label, section, button'))
      .filter((element) => {
        if (!isElementVisible(element)) return false;
        if (element.contains(placeholder)) return false;
        if (!hasLabel(element)) return false;

        const rect = element.getBoundingClientRect();
        if (rect.width < 80 || rect.height < 24 || rect.height > 240) return false;

        const distance = Math.min(
          Math.abs(placeholderRect.top - rect.bottom),
          Math.abs(placeholderRect.bottom - rect.top)
        );

        return distance <= SETTINGS.toggleSearchDistance * 1.5;
      })
      .map((element) => {
        const rect = element.getBoundingClientRect();
        const distance = Math.min(
          Math.abs(placeholderRect.top - rect.bottom),
          Math.abs(placeholderRect.bottom - rect.top)
        );
        return { element, rect, distance };
      })
      .sort((a, b) => {
        if (a.distance !== b.distance) return a.distance - b.distance;
        return (a.rect.width * a.rect.height) - (b.rect.width * b.rect.height);
      });

    return candidates[0]?.element || null;
  }

  function findAssociatedToggleByLabel(kind, placeholder) {
    if (!placeholder) return null;

    const keywords = SETTINGS[kind]?.toggleLabelKeywords || [];
    if (!keywords.length) return null;

    const labeledRegion = findAssociatedUpsellRegionByLabel(kind, placeholder);
    if (labeledRegion) {
      const directControl = findToggleInside(labeledRegion);
      if (directControl) return directControl;
    }

    const placeholderRect = placeholder.getBoundingClientRect();
    const controls = Array.from(document.querySelectorAll(TOGGLE_SELECTOR))
      .filter(isElementVisible);

    const matches = [];

    controls.forEach((control) => {
      let row = control;

      for (let depth = 0; depth < 7 && row; depth += 1) {
        if (row.contains(placeholder)) break;

        const text = elementTextWithoutPiedraInterface(row);
        const labelMatched = keywords.some((keyword) =>
          text.includes(normalize(keyword))
        );

        if (labelMatched) {
          const rect = row.getBoundingClientRect();
          const verticalDistance = Math.abs(
            placeholderRect.top - rect.bottom
          );
          const documentDistance = Math.abs(
            control.compareDocumentPosition(placeholder) &
            Node.DOCUMENT_POSITION_FOLLOWING
              ? placeholderRect.top - rect.bottom
              : placeholderRect.bottom - rect.top
          );

          matches.push({
            control,
            row,
            score: Math.max(0, verticalDistance) + Math.max(0, documentDistance)
          });
          break;
        }

        row = row.parentElement;
      }
    });

    matches.sort((a, b) => a.score - b.score);
    return matches[0]?.control || null;
  }

  function findAssociatedToggle(placeholder, kind) {
    if (!placeholder) return null;

    const labeledControl = findAssociatedToggleByLabel(kind, placeholder);
    if (labeledControl) return labeledControl;

    /*
      The Custom HTML block is expected directly below its upsell.
      Search previous siblings first because that is the most reliable path.
    */
    let branch = placeholder;

    for (let depth = 0; depth < 6 && branch; depth += 1) {
      let sibling = branch.previousElementSibling;
      let inspected = 0;

      while (sibling && inspected < 6) {
        const control = findToggleInside(sibling);
        if (control) return control;

        sibling = sibling.previousElementSibling;
        inspected += 1;
      }

      branch = branch.parentElement;
    }

    /* Geometry fallback for app layouts with nested wrapper blocks. */
    const placeholderRect = placeholder.getBoundingClientRect();
    let container = placeholder.parentElement;

    for (let depth = 0; depth < 5 && container; depth += 1) {
      const candidates = Array.from(
        container.querySelectorAll(TOGGLE_SELECTOR)
      )
        .filter(isElementVisible)
        .map((control) => {
          const rect = control.getBoundingClientRect();
          const verticalDistance = Math.max(
            0,
            placeholderRect.top - rect.bottom
          );
          const overlapsHorizontally =
            rect.right >= placeholderRect.left &&
            rect.left <= placeholderRect.right;

          return {
            control,
            verticalDistance,
            overlapsHorizontally,
            appearsBefore:
              control.compareDocumentPosition(placeholder) &
              Node.DOCUMENT_POSITION_FOLLOWING
          };
        })
        .filter(
          (entry) =>
            entry.appearsBefore &&
            entry.overlapsHorizontally &&
            entry.verticalDistance <= SETTINGS.toggleSearchDistance
        )
        .sort((a, b) => a.verticalDistance - b.verticalDistance);

      if (candidates[0]) {
        return candidates[0].control;
      }

      container = container.parentElement;
    }

    return null;
  }

  function getToggleWrapper(control) {
    if (!control) return null;

    if (control.id) {
      const escapedId = window.CSS?.escape
        ? window.CSS.escape(control.id)
        : control.id.replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');

      const label = document.querySelector(`label[for="${escapedId}"]`);
      if (label) return label;
    }

    return (
      control.closest('label') ||
      control.closest('[class*="toggle" i]') ||
      control.closest('[class*="switch" i]') ||
      control.parentElement ||
      control
    );
  }

  function getNoticeForKind(kind) {
    const placeholder =
      kind === 'engraving'
        ? getEngravingPlaceholder()
        : getColorPlaceholder();

    return {
      placeholder,
      notice: placeholder?.querySelector(
        '.piedra-addon-existing-notice'
      ) || null
    };
  }

  function findToggleTextElement(wrapper, control) {
    if (!(wrapper instanceof Element)) return null;

    const controlRect = control?.getBoundingClientRect();

    const candidates = Array.from(
      wrapper.querySelectorAll('span, p, div, strong, small')
    )
      .filter((element) => {
        if (!isElementVisible(element)) return false;
        if (element.matches('.piedra-addon-click-blocker')) return false;
        if (element.contains(control) && element !== wrapper) return false;

        const text = String(element.textContent || '').trim();
        if (!text) return false;

        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;

        if (controlRect) {
          const separatedFromControl =
            rect.right <= controlRect.left + 4 ||
            rect.left >= controlRect.right - 4;

          if (!separatedFromControl) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return aRect.width - bRect.width;
      });

    return candidates[0] || null;
  }

  function syncNoticeAlignment(kind, placeholder, wrapper, control) {
    if (!placeholder || !wrapper) return;

    const placeholderRect = placeholder.getBoundingClientRect();
    const textElement = findToggleTextElement(wrapper, control);
    const referenceRect =
      textElement?.getBoundingClientRect() ||
      wrapper.getBoundingClientRect();

    const rawOffset = referenceRect.left - placeholderRect.left;
    const maxOffset = Math.max(0, placeholderRect.width * 0.55);
    const offset = Math.min(Math.max(rawOffset, 0), maxOffset);

    placeholder.style.setProperty(
      '--piedra-notice-offset',
      `${Math.round(offset)}px`
    );
  }

  function getLowestCommonAncestor(first, second) {
    if (!(first instanceof Element) || !(second instanceof Element)) {
      return null;
    }

    const ancestors = new Set();
    let node = first;

    while (node) {
      ancestors.add(node);
      node = node.parentElement;
    }

    node = second;

    while (node) {
      if (ancestors.has(node)) return node;
      node = node.parentElement;
    }

    return null;
  }

  function getToggleInteractionRegion(control, placeholder) {
    if (!control || !placeholder) return null;

    const wrapper = getToggleWrapper(control);
    const commonAncestor = getLowestCommonAncestor(
      control,
      placeholder
    );

    let controlBranch = control;

    if (commonAncestor) {
      while (
        controlBranch.parentElement &&
        controlBranch.parentElement !== commonAncestor
      ) {
        controlBranch = controlBranch.parentElement;
      }
    }

    const placeholderRect = placeholder.getBoundingClientRect();
    const controlRect = control.getBoundingClientRect();

    const candidates = [controlBranch, wrapper, control]
      .filter((element, index, list) =>
        element instanceof Element &&
        list.indexOf(element) === index &&
        !element.contains(placeholder)
      )
      .map((element) => {
        const rect = element.getBoundingClientRect();
        const distance = Math.max(
          0,
          placeholderRect.top - rect.bottom
        );

        return { element, rect, distance };
      })
      .filter(({ rect, distance }) =>
        rect.width > 0 &&
        rect.height > 0 &&
        rect.height <= 220 &&
        rect.top < placeholderRect.top + 8 &&
        distance <= SETTINGS.toggleSearchDistance &&
        rect.width >= Math.max(32, controlRect.width)
      )
      .sort((a, b) => {
        if (a.distance !== b.distance) {
          return a.distance - b.distance;
        }

        return b.rect.width - a.rect.width;
      });

    return candidates[0]?.element || wrapper || control;
  }

  function syncPortalShieldRecord(record) {
    if (!record?.overlay?.isConnected) return;

    const region = record.region;

    if (!region?.isConnected || !isElementVisible(region)) {
      record.overlay.style.display = 'none';
      return;
    }

    const rect = region.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      record.overlay.style.display = 'none';
      return;
    }

    record.overlay.style.display = 'block';
    record.overlay.style.left = `${Math.round(rect.left)}px`;
    record.overlay.style.top = `${Math.round(rect.top)}px`;
    record.overlay.style.width = `${Math.round(rect.width)}px`;
    record.overlay.style.height = `${Math.round(rect.height)}px`;
  }

  function syncAllPortalShields() {
    Object.values(state.lockedControls).forEach(
      syncPortalShieldRecord
    );
  }

  function syncPortalShieldBurst() {
    [0, 16, 50, 100, 180, 300, 500, 800].forEach(
      (delay) => setTimeout(syncAllPortalShields, delay)
    );
  }

  function restoreLockedControl(kind) {
    const record = state.lockedControls[kind];
    if (!record) return;

    const {
      control,
      wrapper,
      region,
      overlay,
      original
    } = record;

    overlay?.remove();

    if (control?.isConnected) {
      control.removeAttribute('data-piedra-addon-toggle-locked');
      control.removeAttribute('data-piedra-addon-kind');

      if ('disabled' in control) {
        control.disabled = original.disabled;
      }

      if (original.ariaDisabled === null) {
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('aria-disabled', original.ariaDisabled);
      }

      if (original.tabindex === null) {
        control.removeAttribute('tabindex');
      } else {
        control.setAttribute('tabindex', original.tabindex);
      }

      if (original.ariaChecked === null) {
        control.removeAttribute('aria-checked');
      } else {
        control.setAttribute('aria-checked', original.ariaChecked);
      }

      if (
        original.checked !== null &&
        control instanceof HTMLInputElement &&
        control.type === 'checkbox'
      ) {
        control.checked = original.checked;
      }
    }

    if (wrapper?.isConnected) {
      wrapper.classList.remove('piedra-addon-toggle-locked');
      wrapper.removeAttribute('data-piedra-addon-toggle-locked');
      wrapper.removeAttribute('data-piedra-addon-kind');

      if (original.wrapperAriaDisabled === null) {
        wrapper.removeAttribute('aria-disabled');
      } else {
        wrapper.setAttribute(
          'aria-disabled',
          original.wrapperAriaDisabled
        );
      }

      wrapper.style.position = original.wrapperInlinePosition;
    }

    if (region?.isConnected) {
      region.classList.remove('piedra-addon-toggle-region-locked');
      region.removeAttribute('data-piedra-addon-toggle-locked');
      region.removeAttribute('data-piedra-addon-kind');

      region.style.pointerEvents = original.regionPointerEvents;
      region.style.userSelect = original.regionUserSelect;
      region.style.webkitUserSelect = original.regionWebkitUserSelect;
      region.style.touchAction = original.regionTouchAction;

      if (original.regionAriaDisabled === null) {
        region.removeAttribute('aria-disabled');
      } else {
        region.setAttribute(
          'aria-disabled',
          original.regionAriaDisabled
        );
      }

      if (original.regionInert) {
        region.setAttribute('inert', '');
      } else {
        region.removeAttribute('inert');
      }
    }

    state.lockedControls[kind] = null;
  }

  function forceToggleOff(control, wrapper = null) {
    if (!control && !wrapper) return;

    const roots = [control, wrapper].filter(
      (element, index, list) =>
        element instanceof Element && list.indexOf(element) === index
    );

    const targets = [];

    roots.forEach((root) => {
      targets.push(root);
      targets.push(
        ...root.querySelectorAll(
          'input[type="checkbox"], [role="switch"], [role="checkbox"], [aria-checked], [data-state]'
        )
      );
    });

    targets
      .filter((element, index, list) => list.indexOf(element) === index)
      .forEach((element) => {
        if (
          element instanceof HTMLInputElement &&
          element.type === 'checkbox'
        ) {
          element.checked = false;
          element.defaultChecked = false;
          element.removeAttribute('checked');
        }

        if (
          element.matches(
            '[role="switch"], [role="checkbox"], [aria-checked]'
          )
        ) {
          element.setAttribute('aria-checked', 'false');
        }

        if (element.hasAttribute('data-state')) {
          element.setAttribute('data-state', 'unchecked');
        }

        if (element.hasAttribute('data-checked')) {
          element.setAttribute('data-checked', 'false');
        }
      });
  }

  function createPortalShield(kind) {
    document.querySelectorAll(
      `.piedra-addon-portal-shield[data-piedra-addon-kind="${kind}"]`
    ).forEach((element) => element.remove());

    const overlay = document.createElement('span');
    overlay.className = 'piedra-addon-portal-shield';
    overlay.setAttribute('data-piedra-addon-kind', kind);
    overlay.setAttribute('aria-hidden', 'true');

    Object.assign(overlay.style, {
      position: 'fixed',
      zIndex: '2147483647',
      display: 'block',
      margin: '0',
      padding: '0',
      border: '0',
      borderRadius: '0',
      background: 'transparent',
      boxShadow: 'none',
      cursor: 'not-allowed',
      pointerEvents: 'auto',
      touchAction: 'none'
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function setAssociatedToggleLocked(kind, placeholder, shouldLock) {
    if (!shouldLock) {
      restoreLockedControl(kind);
      return;
    }

    const labeledRegion = findAssociatedUpsellRegionByLabel(
      kind,
      placeholder
    );

    const control =
      findAssociatedToggle(placeholder, kind) ||
      labeledRegion?.querySelector('input[type="checkbox"], button, [role="switch"], [role="checkbox"]') ||
      labeledRegion;

    if (!control) return;

    const wrapper = labeledRegion || getToggleWrapper(control);
    if (!wrapper) return;

    const region =
      labeledRegion ||
      getToggleInteractionRegion(control, placeholder);

    if (!region) return;

    const currentRecord = state.lockedControls[kind];

    if (
      currentRecord?.control === control &&
      currentRecord?.wrapper === wrapper &&
      currentRecord?.region === region &&
      currentRecord?.overlay?.isConnected
    ) {
      forceToggleOff(control, wrapper);
      control.setAttribute('aria-disabled', 'true');
      wrapper.setAttribute('aria-disabled', 'true');
      region.setAttribute('aria-disabled', 'true');
      region.setAttribute('inert', '');
      region.style.pointerEvents = 'none';

      if ('disabled' in control) {
        control.disabled = true;
      }

      syncNoticeAlignment(kind, placeholder, wrapper, control);
      syncPortalShieldRecord(currentRecord);
      return;
    }

    restoreLockedControl(kind);

    const original = {
      disabled: 'disabled' in control ? Boolean(control.disabled) : false,
      ariaDisabled: control.getAttribute('aria-disabled'),
      tabindex: control.getAttribute('tabindex'),
      ariaChecked: control.getAttribute('aria-checked'),
      checked:
        control instanceof HTMLInputElement &&
        control.type === 'checkbox'
          ? control.checked
          : null,
      wrapperAriaDisabled: wrapper.getAttribute('aria-disabled'),
      wrapperInlinePosition: wrapper.style.position,
      regionAriaDisabled: region.getAttribute('aria-disabled'),
      regionInert: region.hasAttribute('inert'),
      regionPointerEvents: region.style.pointerEvents,
      regionUserSelect: region.style.userSelect,
      regionWebkitUserSelect: region.style.webkitUserSelect,
      regionTouchAction: region.style.touchAction
    };

    forceToggleOff(control, wrapper);

    control.setAttribute('data-piedra-addon-toggle-locked', 'true');
    control.setAttribute('data-piedra-addon-kind', kind);
    control.setAttribute('aria-disabled', 'true');
    control.setAttribute('tabindex', '-1');

    if ('disabled' in control) {
      control.disabled = true;
    }

    wrapper.classList.add('piedra-addon-toggle-locked');
    wrapper.setAttribute('data-piedra-addon-toggle-locked', 'true');
    wrapper.setAttribute('data-piedra-addon-kind', kind);
    wrapper.setAttribute('aria-disabled', 'true');

    region.classList.add('piedra-addon-toggle-region-locked');
    region.setAttribute('data-piedra-addon-toggle-locked', 'true');
    region.setAttribute('data-piedra-addon-kind', kind);
    region.setAttribute('aria-disabled', 'true');
    region.setAttribute('inert', '');
    region.style.pointerEvents = 'none';
    region.style.userSelect = 'none';
    region.style.webkitUserSelect = 'none';
    region.style.touchAction = 'none';

    const overlay = createPortalShield(kind);

    state.lockedControls[kind] = {
      control,
      wrapper,
      region,
      overlay,
      original
    };

    syncNoticeAlignment(kind, placeholder, wrapper, control);
    syncPortalShieldRecord(state.lockedControls[kind]);
    syncPortalShieldBurst();
  }

  function getBlockedToggleKind(target) {
    if (!(target instanceof Element)) return null;

    const blockedElement = target.closest(
      '.piedra-addon-portal-shield, ' +
      '[data-piedra-addon-toggle-locked="true"], ' +
      '.piedra-addon-toggle-region-locked, ' +
      '.piedra-addon-toggle-locked'
    );

    if (!blockedElement) return null;

    return (
      blockedElement.getAttribute('data-piedra-addon-kind') ||
      blockedElement.querySelector('[data-piedra-addon-kind]')
        ?.getAttribute('data-piedra-addon-kind') ||
      null
    );
  }

  // Locked-option notices stay hidden until the shopper actually tries
  // to interact with the unavailable toggle.
  function pulseExistingNotice(kind) {
    const { notice } = getNoticeForKind(kind);

    if (!notice) return;

    state.noticeShown[kind] = true;
    setAnimatedVisibility(notice, true);

    if (typeof notice.animate === 'function') {
      notice.animate(
        [
          { transform: 'translateY(0)', opacity: 0.65 },
          { transform: 'translateY(0)', opacity: 1 }
        ],
        { duration: 180, easing: 'ease-out' }
      );
    }
  }

  /* -------------------- TARGET PICKER HELPERS -------------------- */

  function getTargetParts(kind) {
    const parts =
      kind === 'engraving'
        ? getEngravingParts()
        : getColorParts();

    return {
      ...parts,
      targetPicker: parts.placeholder?.querySelector(
        `[data-piedra-target-picker="${kind}"]`
      ) || null,
      targetSelect: parts.placeholder?.querySelector(
        `[data-piedra-target-select="${kind}"]`
      ) || null,
      targetError: parts.placeholder?.querySelector(
        `[data-piedra-target-error="${kind}"]`
      ) || null
    };
  }

  function targetDisplayLabel(item) {
    const productTitle = String(
      item?.product_title || item?.title || 'Item'
    ).trim();
    const variantTitle = String(item?.variant_title || '').trim();

    if (
      variantTitle &&
      normalize(variantTitle) !== 'default title' &&
      !normalize(productTitle).endsWith(normalize(variantTitle))
    ) {
      return `${productTitle} - ${variantTitle}`;
    }

    return productTitle;
  }

  function buildTargetOptions(targets) {
    const counts = new Map();

    return targets.map((item) => {
      const baseLabel = targetDisplayLabel(item);
      const count = (counts.get(baseLabel) || 0) + 1;
      counts.set(baseLabel, count);

      return {
        key: String(item.key || ''),
        label: count > 1 ? `${baseLabel} (${count})` : baseLabel,
        item
      };
    });
  }

  function clearTargetError(kind) {
    const { targetPicker, targetSelect, targetError } = getTargetParts(kind);
    targetPicker?.classList.remove('is-invalid');
    targetSelect?.removeAttribute('aria-invalid');
    if (targetError) targetError.hidden = true;
  }

  function markTargetMissing(kind, { showMessage = false, focus = false } = {}) {
    const { targetPicker, targetSelect, targetError } = getTargetParts(kind);
    if (!targetSelect) return;

    targetPicker?.classList.add('is-invalid');
    targetSelect.setAttribute('aria-invalid', 'true');
    if (targetError) targetError.hidden = !showMessage;
    if (focus) targetSelect.focus();
  }

  function renderTargetPicker(kind, targets, addonActive, preferredKey = '') {
    const { targetPicker, targetSelect } = getTargetParts(kind);
    if (!targetPicker || !targetSelect) return '';

    const options = buildTargetOptions(targets);
    const validKeys = new Set(options.map((option) => option.key));

    let selectedKey = String(
      state.pendingTargetKey[kind] ||
      preferredKey ||
      state.selectedTargetKey[kind] ||
      ''
    );

    if (!validKeys.has(selectedKey)) {
      selectedKey = '';
    }

    if (addonActive && options.length === 1) {
      selectedKey = options[0].key;
    }

    const optionSignature = options
      .map((option) => `${option.key}::${option.label}`)
      .join('||');

    if (targetSelect.dataset.piedraOptionSignature !== optionSignature) {
      targetSelect.innerHTML = '';

      const prompt = document.createElement('option');
      prompt.value = '';
      prompt.textContent = 'Choose an item';
      targetSelect.appendChild(prompt);

      options.forEach((option) => {
        const element = document.createElement('option');
        element.value = option.key;
        element.textContent = option.label;
        targetSelect.appendChild(element);
      });

      targetSelect.dataset.piedraOptionSignature = optionSignature;
    }

    targetSelect.value = selectedKey;
    state.selectedTargetKey[kind] = selectedKey;

    const shouldShow = addonActive && options.length > 1;
    targetPicker.hidden = !shouldShow;
    targetPicker.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

    if (shouldShow) {
      targetSelect.setAttribute('required', '');
    } else {
      targetSelect.removeAttribute('required');
    }

    if (shouldShow && !selectedKey) {
      markTargetMissing(kind);
    } else {
      clearTargetError(kind);
    }

    return selectedKey;
  }

  /* -------------------- ENGRAVING INTERFACE -------------------- */

  function buildEngravingInterface(placeholder) {
    if (!placeholder) return null;

    let field = placeholder.querySelector('.piedra-engraving-field');
    if (field) return field;

    placeholder.innerHTML = `
      <div
        class="piedra-addon-existing-notice"
        role="status"
        aria-live="polite"
        hidden
      >${SETTINGS.engraving.existingMessage}</div>

      <div class="piedra-engraving-field" hidden>
        <div
          class="piedra-target-picker"
          data-piedra-target-picker="engraving"
          hidden
        >
          <label for="piedra-engraving-target">Apply engraving to</label>
          <select
            id="piedra-engraving-target"
            data-piedra-target-select="engraving"
            aria-describedby="piedra-engraving-target-error"
          >
            <option value="">Choose an item</option>
          </select>
          <p
            id="piedra-engraving-target-error"
            class="piedra-target-error"
            data-piedra-target-error="engraving"
            hidden
          >Please choose which item the engraving applies to.</p>
        </div>

        <label for="piedra-essential-engraving-input">
          Custom engraving text
        </label>

        <input
          id="piedra-essential-engraving-input"
          type="text"
          maxlength="${SETTINGS.engraving.maxCharacters}"
          placeholder="Enter the text exactly as it should appear"
          autocomplete="off"
          aria-describedby="piedra-essential-engraving-meta piedra-essential-engraving-error"
        >

        <div
          id="piedra-essential-engraving-meta"
          class="piedra-engraving-meta"
        >
          <span>Maximum ${SETTINGS.engraving.maxCharacters} characters</span>
          <span class="piedra-engraving-count">
            0/${SETTINGS.engraving.maxCharacters}
          </span>
        </div>

        <p
          id="piedra-essential-engraving-error"
          class="piedra-engraving-error"
          hidden
        >
          Please enter the engraving text before checkout.
        </p>
      </div>
    `;

    field = placeholder.querySelector('.piedra-engraving-field');
    const input = placeholder.querySelector(
      '.piedra-engraving-field input[type="text"]'
    );
    const targetSelect = placeholder.querySelector(
      '[data-piedra-target-select="engraving"]'
    );

    targetSelect?.addEventListener('change', () => {
      state.selectedTargetKey.engraving = targetSelect.value;
      state.pendingTargetKey.engraving = targetSelect.value;
      clearTargetError('engraving');
      persistTargetSelection('engraving');
    });

    input?.addEventListener('input', () => {
      updateEngravingCharacterCount(placeholder);

      if (input.value.trim()) {
        clearEngravingError(placeholder);
      } else {
        markEngravingEmpty(placeholder);
      }

      scheduleEngravingSave();
    });

    input?.addEventListener('blur', () => {
      clearTimeout(state.engravingSaveTimer);

      if (!input.value.trim()) {
        markEngravingEmpty(placeholder);
        return;
      }

      saveEngravingText();
    });

    return field;
  }

  function getEngravingParts() {
    const placeholder = getEngravingPlaceholder();
    if (!placeholder) return {};

    buildEngravingInterface(placeholder);

    return {
      placeholder,
      field: placeholder.querySelector('.piedra-engraving-field'),
      notice: placeholder.querySelector('.piedra-addon-existing-notice'),
      input: placeholder.querySelector(
        '.piedra-engraving-field input[type="text"]'
      ),
      count: placeholder.querySelector('.piedra-engraving-count'),
      error: placeholder.querySelector('.piedra-engraving-error')
    };
  }

  function updateEngravingCharacterCount(
    placeholder = getEngravingPlaceholder()
  ) {
    const input = placeholder?.querySelector(
      '.piedra-engraving-field input[type="text"]'
    );
    const count = placeholder?.querySelector(
      '.piedra-engraving-count'
    );

    if (input && count) {
      count.textContent =
        `${input.value.length}/${SETTINGS.engraving.maxCharacters}`;
    }
  }

  function clearEngravingError(
    placeholder = getEngravingPlaceholder()
  ) {
    const input = placeholder?.querySelector(
      '.piedra-engraving-field input[type="text"]'
    );
    const error = placeholder?.querySelector(
      '.piedra-engraving-error'
    );

    input?.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
  }

  function markEngravingEmpty(
    placeholder = getEngravingPlaceholder(),
    { showMessage = false, focus = false } = {}
  ) {
    const input = placeholder?.querySelector(
      '.piedra-engraving-field input[type="text"]'
    );
    const error = placeholder?.querySelector(
      '.piedra-engraving-error'
    );

    if (!input) return;

    input.setAttribute('aria-invalid', 'true');
    if (error) error.hidden = !showMessage;
    if (focus) input.focus();
  }

  /* ---------------------- COLOR INTERFACE ---------------------- */

  function buildColorInterface(placeholder) {
    if (!placeholder) return null;

    let field = placeholder.querySelector('.piedra-color-field');
    if (field) return field;

    state.colorInstanceCount += 1;
    const instanceId = state.colorInstanceCount;
    const groupName = `piedra-color-selection-${instanceId}`;
    const errorId = `piedra-color-error-${instanceId}`;
    const targetId = `piedra-color-target-${instanceId}`;
    const targetErrorId = `piedra-color-target-error-${instanceId}`;

    const optionsHtml = SETTINGS.color.options
      .map((option) => `
        <label class="piedra-color-option">
          <input
            type="radio"
            name="${groupName}"
            value="${option.name}"
            aria-describedby="${errorId}"
          >
          <span class="piedra-color-option-inner">
            <span
              class="piedra-color-swatch"
              style="--piedra-swatch-color: ${option.hex};"
              aria-hidden="true"
            ></span>
            <span class="piedra-color-name">${option.name}</span>
          </span>
        </label>
      `)
      .join('');

    placeholder.innerHTML = `
      <div
        class="piedra-addon-existing-notice"
        role="status"
        aria-live="polite"
        hidden
      >${SETTINGS.color.existingMessage}</div>

      <div class="piedra-color-field" hidden>
        <div
          class="piedra-target-picker"
          data-piedra-target-picker="color"
          hidden
        >
          <label for="${targetId}">Apply color to</label>
          <select
            id="${targetId}"
            data-piedra-target-select="color"
            aria-describedby="${targetErrorId}"
          >
            <option value="">Choose an item</option>
          </select>
          <p
            id="${targetErrorId}"
            class="piedra-target-error"
            data-piedra-target-error="color"
            hidden
          >Please choose which item the color applies to.</p>
        </div>

        <fieldset class="piedra-color-fieldset">
          <legend class="piedra-color-legend">Choose your color</legend>

          <div class="piedra-color-swatches">
            ${optionsHtml}
          </div>

          <p
            id="${errorId}"
            class="piedra-color-error"
            hidden
          >
            Please choose a color before checkout.
          </p>
        </fieldset>
      </div>
    `;

    field = placeholder.querySelector('.piedra-color-field');
    const targetSelect = placeholder.querySelector(
      '[data-piedra-target-select="color"]'
    );

    targetSelect?.addEventListener('change', () => {
      state.selectedTargetKey.color = targetSelect.value;
      state.pendingTargetKey.color = targetSelect.value;
      clearTargetError('color');
      persistTargetSelection('color');
    });

    placeholder
      .querySelectorAll('.piedra-color-field input[type="radio"]')
      .forEach((input) => {
        input.addEventListener('change', () => {
          state.pendingColor = input.value;
          clearColorError(placeholder);
          saveColorSelection(input.value);
        });
      });

    return field;
  }

  function getColorParts() {
    const placeholder = getColorPlaceholder();
    if (!placeholder) return {};

    buildColorInterface(placeholder);

    return {
      placeholder,
      field: placeholder.querySelector('.piedra-color-field'),
      notice: placeholder.querySelector('.piedra-addon-existing-notice'),
      radios: Array.from(
        placeholder.querySelectorAll(
          '.piedra-color-field input[type="radio"]'
        )
      ),
      error: placeholder.querySelector('.piedra-color-error')
    };
  }

  function getSelectedColor() {
    const { placeholder } = getColorParts();

    return (
      placeholder?.querySelector(
        '.piedra-color-field input[type="radio"]:checked'
      )?.value || ''
    );
  }

  function selectStoredColor(value) {
    const { radios } = getColorParts();
    const normalizedValue = normalize(value);

    radios?.forEach((radio) => {
      radio.checked = normalize(radio.value) === normalizedValue;
    });
  }

  function clearColorSelection() {
    const { radios } = getColorParts();
    radios?.forEach((radio) => {
      radio.checked = false;
    });
  }

  function clearColorError(placeholder = getColorPlaceholder()) {
    const field = placeholder?.querySelector('.piedra-color-field');
    const error = placeholder?.querySelector('.piedra-color-error');

    field?.classList.remove('is-invalid');
    field?.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
  }

  function markColorEmpty(
    placeholder = getColorPlaceholder(),
    { showMessage = false, focus = false } = {}
  ) {
    const field = placeholder?.querySelector('.piedra-color-field');
    const error = placeholder?.querySelector('.piedra-color-error');
    const firstRadio = placeholder?.querySelector(
      '.piedra-color-field input[type="radio"]'
    );

    if (!field) return;

    field.classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');
    if (error) error.hidden = !showMessage;
    if (focus) firstRadio?.focus();
  }

  /* ------------------------- CART DATA ------------------------- */

  async function getCart() {
    const response = await fetch(`${rootUrl()}cart.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart: ${response.status}`);
    }

    return response.json();
  }

  function stringId(value) {
    return String(value ?? '').trim();
  }

  function itemMatchesKeywords(item, keywords) {
    const searchableText = normalize(
      [
        item.product_title,
        item.title,
        item.variant_title,
        item.handle,
        item.sku
      ]
        .filter(Boolean)
        .join(' ')
    );

    return keywords.some((keyword) =>
      searchableText.includes(normalize(keyword))
    );
  }

  function itemMatchesExactAddon(item, kind) {
    const config = SETTINGS[kind];
    const variantId = stringId(item?.variant_id || item?.id);
    const productId = stringId(item?.product_id);

    return (
      config.cartAddonVariantIds.includes(variantId) ||
      config.cartAddonProductIds.includes(productId)
    );
  }

  function isEngravingLikeItem(item) {
    return itemMatchesKeywords(
      item,
      SETTINGS.engraving.productKeywords
    );
  }

  function isColorLikeItem(item) {
    return itemMatchesKeywords(item, SETTINGS.color.productKeywords);
  }

  function isCartEngravingAddon(item) {
    return itemMatchesExactAddon(item, 'engraving');
  }

  function isCartColorAddon(item) {
    return itemMatchesExactAddon(item, 'color');
  }

  function isPiedraAddonItem(item) {
    return (
      isCartEngravingAddon(item) ||
      isCartColorAddon(item) ||
      isEngravingLikeItem(item) ||
      isColorLikeItem(item)
    );
  }

  function findEngravingItem(cart) {
    return (cart?.items || []).find(isCartEngravingAddon) || null;
  }

  function findColorItem(cart) {
    return (cart?.items || []).find(isCartColorAddon) || null;
  }

  function getMainItems(cart) {
    return (cart?.items || []).filter(
      (item) => !isPiedraAddonItem(item) && !item?.gift_card
    );
  }

  function getStoredProperty(item, propertyName) {
    return String(item?.properties?.[propertyName] || '');
  }

  function getPropertyEntries(item) {
    const properties = item?.properties;

    if (!properties || typeof properties !== 'object') {
      return [];
    }

    return Object.entries(properties).filter(
      ([key, value]) => key && value !== null && value !== undefined
    );
  }

  function valueMeansSelected(value) {
    const normalizedValue = normalize(value);
    return !SETTINGS.falseLikeValues.includes(normalizedValue);
  }

  function itemHasExistingEngravingProperty(item) {
    if (isPiedraAddonItem(item)) return false;

    return getPropertyEntries(item).some(([key, value]) => {
      const normalizedKey = normalize(key);
      const isEngravingProperty =
        SETTINGS.engraving.existingPropertyKeywords.some((keyword) =>
          normalizedKey.includes(normalize(keyword))
        );

      return isEngravingProperty && valueMeansSelected(value);
    });
  }

  function getNativeColorValues() {
    return [
      ...SETTINGS.color.options.map((option) => option.name),
      ...(SETTINGS.color.legacyNativeColorNames || [])
    ].map(normalize);
  }

  function textContainsKnownColor(value) {
    const normalizedValue = normalize(value);
    if (!normalizedValue || normalizedValue === 'default title') {
      return false;
    }

    return getNativeColorValues().some(
      (colorName) =>
        normalizedValue === colorName ||
        normalizedValue.includes(colorName)
    );
  }

  function textContainsBaseColor(value) {
    const normalizedValue = normalize(value);

    return SETTINGS.color.baseVariantValues.some(
      (baseName) =>
        normalizedValue === normalize(baseName) ||
        normalizedValue.includes(normalize(baseName))
    );
  }

  function itemHasNativeColorVariant(item) {
    if (isPiedraAddonItem(item)) return false;

    const optionPairs = Array.isArray(item?.options_with_values)
      ? item.options_with_values
      : [];

    if (
      optionPairs.some((option) =>
        textContainsKnownColor(option?.value)
      )
    ) {
      return true;
    }

    const variantOptions = Array.isArray(item?.variant_options)
      ? item.variant_options
      : [];

    return (
      textContainsKnownColor(item?.variant_title) ||
      variantOptions.some(textContainsKnownColor)
    );
  }

  function itemIsColorCapable(item) {
    if (isPiedraAddonItem(item)) return false;

    const optionPairs = Array.isArray(item?.options_with_values)
      ? item.options_with_values
      : [];

    const hasNamedColorOption = optionPairs.some((option) => {
      const optionName = normalize(option?.name);
      return SETTINGS.color.nativeOptionNames.some((name) =>
        optionName.includes(normalize(name))
      );
    });

    const variantOptions = Array.isArray(item?.variant_options)
      ? item.variant_options
      : [];

    return (
      hasNamedColorOption ||
      textContainsKnownColor(item?.variant_title) ||
      textContainsBaseColor(item?.variant_title) ||
      variantOptions.some(
        (value) =>
          textContainsKnownColor(value) ||
          textContainsBaseColor(value)
      )
    );
  }

  function getAddonTargetKey(addonItem, mainItems) {
    if (!addonItem) return '';

    const properties = addonItem?.properties || {};
    const storedKey = stringId(
      properties[SETTINGS.target.keyPropertyName]
    );

    if (storedKey && mainItems.some((item) => item.key === storedKey)) {
      return storedKey;
    }

    const parentKey = stringId(
      addonItem?.parent_relationship?.parent_key
    );

    if (parentKey && mainItems.some((item) => item.key === parentKey)) {
      return parentKey;
    }

    const storedVariant = stringId(
      properties[SETTINGS.target.variantPropertyName]
    );

    if (storedVariant) {
      const matches = mainItems.filter(
        (item) => stringId(item.variant_id || item.id) === storedVariant
      );
      if (matches.length === 1) return matches[0].key;
    }

    const storedProduct = stringId(
      properties[SETTINGS.target.productPropertyName]
    );

    if (storedProduct) {
      const matches = mainItems.filter(
        (item) => stringId(item.product_id) === storedProduct
      );
      if (matches.length === 1) return matches[0].key;
    }

    const storedDisplay = normalize(
      properties[SETTINGS.target.displayPropertyName]
    );

    if (storedDisplay) {
      const matches = mainItems.filter(
        (item) => normalize(targetDisplayLabel(item)) === storedDisplay
      );
      if (matches.length === 1) return matches[0].key;
    }

    return '';
  }

  function inferMainTargetFromAddon(addonItem, mainItems, cartItems) {
    const directKey = getAddonTargetKey(addonItem, mainItems);
    if (directKey) return directKey;

    const propertyText = normalize(
      getPropertyEntries(addonItem)
        .flatMap(([key, value]) => [key, value])
        .join(' ')
    );

    const textMatches = mainItems.filter((mainItem) => {
      const clues = [
        mainItem.key,
        mainItem.variant_id,
        mainItem.product_id,
        mainItem.product_title,
        mainItem.variant_title,
        mainItem.handle,
        mainItem.sku
      ]
        .filter(Boolean)
        .map(normalize)
        .filter((value) => value.length >= 3);

      return clues.some((clue) => propertyText.includes(clue));
    });

    if (textMatches.length === 1) {
      return textMatches[0].key;
    }

    const addonIndex = cartItems.indexOf(addonItem);
    if (addonIndex >= 0) {
      const ranked = mainItems
        .map((mainItem) => ({
          item: mainItem,
          index: cartItems.indexOf(mainItem)
        }))
        .filter((entry) => entry.index >= 0)
        .sort((a, b) => {
          const aDistance = Math.abs(a.index - addonIndex);
          const bDistance = Math.abs(b.index - addonIndex);

          if (aDistance !== bDistance) return aDistance - bDistance;

          const aBefore = a.index < addonIndex ? 0 : 1;
          const bBefore = b.index < addonIndex ? 0 : 1;
          return aBefore - bBefore;
        });

      if (ranked[0]) return ranked[0].item.key;
    }

    if (mainItems.length === 1) return mainItems[0].key;

    return '';
  }

  function getProductPageEngravedKeys(cart, mainItems) {
    const keys = new Set(
      mainItems
        .filter(itemHasExistingEngravingProperty)
        .map((item) => item.key)
    );

    const cartItems = cart?.items || [];
    const easifyAddons = cartItems.filter(
      (item) => isEngravingLikeItem(item) && !isCartEngravingAddon(item)
    );

    easifyAddons.forEach((addonItem) => {
      const targetKey = inferMainTargetFromAddon(
        addonItem,
        mainItems,
        cartItems
      );

      if (targetKey) keys.add(targetKey);
    });

    return keys;
  }

  function analyzeCartForKind(cart, kind) {
    const mainItems = getMainItems(cart);
    const addon =
      kind === 'engraving'
        ? findEngravingItem(cart)
        : findColorItem(cart);

    const candidates =
      kind === 'color'
        ? mainItems.filter(itemIsColorCapable)
        : mainItems;

    const customizedKeys =
      kind === 'color'
        ? new Set(
            candidates
              .filter(itemHasNativeColorVariant)
              .map((item) => item.key)
          )
        : getProductPageEngravedKeys(cart, candidates);

    const eligible = candidates.filter(
      (item) => !customizedKeys.has(item.key)
    );

    const assignedTargetKey = getAddonTargetKey(addon, eligible);
    const shouldLock =
      !addon && candidates.length > 0 && eligible.length === 0;

    return {
      kind,
      mainItems,
      candidates,
      customizedKeys,
      eligible,
      addon,
      assignedTargetKey,
      shouldLock
    };
  }

  function getSelectedTarget(kind, analysis) {
    const requestedKey = String(
      state.pendingTargetKey[kind] ||
      state.selectedTargetKey[kind] ||
      analysis?.assignedTargetKey ||
      ''
    );

    const targets = analysis?.eligible || [];
    let target = targets.find((item) => item.key === requestedKey) || null;

    if (!target && targets.length === 1) {
      target = targets[0];
    }

    return target;
  }

  function getTargetProperties(target) {
    if (!target) return {};

    return {
      [SETTINGS.target.displayPropertyName]: targetDisplayLabel(target),
      [SETTINGS.target.keyPropertyName]: String(target.key || ''),
      [SETTINGS.target.variantPropertyName]: stringId(
        target.variant_id || target.id
      ),
      [SETTINGS.target.productPropertyName]: stringId(target.product_id),
      [SETTINGS.target.sourcePropertyName]: SETTINGS.target.sourceValue
    };
  }

  async function changeLineQuantity(item, quantity) {
    const response = await fetch(`${rootUrl()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: item.key,
        quantity
      })
    });

    if (!response.ok) {
      throw new Error(`Unable to change add-on quantity: ${response.status}`);
    }

    return response.json();
  }

  function announceCartUpdated(cart) {
    const detail = {
      cart,
      source: 'piedra-cart-addons'
    };

    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:changed'
    ].forEach((eventName) => {
      document.dispatchEvent(
        new CustomEvent(eventName, { detail })
      );

      window.dispatchEvent(
        new CustomEvent(eventName, { detail })
      );
    });
  }

  async function removeImpossibleAddon(cart, kind, analysis = null) {
    const currentAnalysis = analysis || analyzeCartForKind(cart, kind);

    if (
      !currentAnalysis.addon ||
      currentAnalysis.eligible.length > 0
    ) {
      return { changed: false, cart };
    }

    const updatedCart = await changeLineQuantity(
      currentAnalysis.addon,
      0
    );

    announceCartUpdated(updatedCart);
    return { changed: true, cart: updatedCart };
  }

  function queueDuplicateEnforcement(kind) {
    const run = async () => {
      const cart = await getCart();
      const analysis = analyzeCartForKind(cart, kind);

      if (analysis.addon && analysis.eligible.length === 0) {
        await removeImpossibleAddon(cart, kind, analysis);
      }

      const latestCart = await getCart();
      const latestAnalysis = analyzeCartForKind(latestCart, kind);

      if (latestAnalysis.shouldLock) {
        const record = state.lockedControls[kind];
        forceToggleOff(record?.control, record?.wrapper);
      }

      queueRefresh(60);
    };

    [0, 80, 220, 600, 1200].forEach((delay) => {
      setTimeout(() => {
        state.duplicateCleanupPromise = (
          state.duplicateCleanupPromise || Promise.resolve()
        )
          .catch(() => undefined)
          .then(run)
          .catch((error) => {
            console.error('[Piedra duplicate prevention]', error);
          });
      }, delay);
    });
  }

  function enqueueAddonWrite(kind, task) {
    const previous = state.writeQueues[kind] || Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(task);

    state.writeQueues[kind] = next;
    return next;
  }

  async function writeAddonProperties(kind, propertiesToMerge) {
    return enqueueAddonWrite(kind, async () => {
      try {
        const cart = await getCart();
        const analysis = analyzeCartForKind(cart, kind);
        const item = analysis.addon;

        if (!item) {
          throw new Error(`${kind} add-on is no longer in the cart.`);
        }

        const target = getSelectedTarget(kind, analysis);

        if (!target) {
          markTargetMissing(kind);
          return false;
        }

        state.selectedTargetKey[kind] = target.key;

        const existingProperties =
          item.properties && typeof item.properties === 'object'
            ? item.properties
            : {};

        const response = await fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: {
              ...existingProperties,
              ...getTargetProperties(target),
              ...propertiesToMerge
            }
          })
        });

        if (!response.ok) {
          throw new Error(
            `Unable to save ${kind} selection: ${response.status}`
          );
        }

        return true;
      } catch (error) {
        console.error('[Piedra add-ons]', error);
        return false;
      }
    });
  }

  async function persistTargetSelection(kind) {
    const key = String(state.selectedTargetKey[kind] || '');
    if (!key || !state.items[kind]) return false;

    const merge = {};

    if (kind === 'engraving') {
      const value = getEngravingParts().input?.value.trim() || '';
      if (value) merge[SETTINGS.engraving.propertyName] = value;
    } else {
      const value = getSelectedColor();
      if (value) merge[SETTINGS.color.propertyName] = value;
    }

    const saved = await writeAddonProperties(kind, merge);

    if (saved && state.pendingTargetKey[kind] === key) {
      state.pendingTargetKey[kind] = '';
    }

    if (saved) queueRefresh(140);
    return saved;
  }

  /* --------------------------- SAVING -------------------------- */

  function scheduleEngravingSave() {
    clearTimeout(state.engravingSaveTimer);
    state.engravingSaveTimer = setTimeout(
      saveEngravingText,
      SETTINGS.saveDelay
    );
  }

  async function saveEngravingText() {
    const { input } = getEngravingParts();

    if (
      !input ||
      !state.items.engraving ||
      state.existingSelections.engraving
    ) {
      return false;
    }

    const value = input.value.trim();

    if (!value) {
      markEngravingEmpty();
      return false;
    }

    if (!state.selectedTargetKey.engraving) {
      const analysis = analyzeCartForKind(
        await getCart(),
        'engraving'
      );
      const target = getSelectedTarget('engraving', analysis);
      if (target) state.selectedTargetKey.engraving = target.key;
    }

    if (!state.selectedTargetKey.engraving) {
      markTargetMissing('engraving');
      return false;
    }

    const saved = await writeAddonProperties('engraving', {
      [SETTINGS.engraving.propertyName]: value
    });

    if (saved) {
      state.lastSaved.engraving = value;
      clearEngravingError();
      clearTargetError('engraving');
    }

    return saved;
  }

  async function saveColorSelection(explicitValue = '') {
    if (
      !state.items.color ||
      state.existingSelections.color
    ) {
      return false;
    }

    const value = explicitValue || state.pendingColor || getSelectedColor();

    if (!value) {
      markColorEmpty();
      return false;
    }

    if (!state.selectedTargetKey.color) {
      const analysis = analyzeCartForKind(
        await getCart(),
        'color'
      );
      const target = getSelectedTarget('color', analysis);
      if (target) state.selectedTargetKey.color = target.key;
    }

    if (!state.selectedTargetKey.color) {
      markTargetMissing('color');
      return false;
    }

    const saved = await writeAddonProperties('color', {
      [SETTINGS.color.propertyName]: value
    });

    if (saved) {
      state.lastSaved.color = value;
      if (state.pendingColor === value) {
        state.pendingColor = null;
      }
      clearColorError();
      clearTargetError('color');
    }

    return saved;
  }

  /* -------------------------- REFRESH -------------------------- */

  function refreshEngravingInterface(analysis) {
    const item = analysis.addon;
    const shouldLock = analysis.shouldLock;

    state.existingSelections.engraving = shouldLock;
    state.items.engraving = item;
    state.eligibleTargets.engraving = analysis.eligible;

    const { placeholder, field, notice, input } = getEngravingParts();
    if (!placeholder || !field || !notice || !input) return;

    setNoticeContent(notice, SETTINGS.engraving.existingMessage);
    setAssociatedToggleLocked('engraving', placeholder, shouldLock);

    if (shouldLock) {
      setAnimatedVisibility(field, false);
      setAnimatedVisibility(
        notice,
        Boolean(state.noticeShown.engraving)
      );
      state.lastSaved.engraving = null;
      state.selectedTargetKey.engraving = '';
      state.pendingTargetKey.engraving = '';
      clearEngravingError();
      clearTargetError('engraving');
      return;
    }

    state.noticeShown.engraving = false;
    setAnimatedVisibility(notice, false);

    if (!item) {
      setAnimatedVisibility(field, false);
      state.lastSaved.engraving = null;
      state.selectedTargetKey.engraving = '';
      state.pendingTargetKey.engraving = '';
      clearEngravingError();
      clearTargetError('engraving');
      return;
    }

    setAnimatedVisibility(field, true);

    const assignedTarget = getAddonTargetKey(item, analysis.eligible);
    const selectedKey = renderTargetPicker(
      'engraving',
      analysis.eligible,
      true,
      assignedTarget
    );

    if (
      selectedKey &&
      !assignedTarget &&
      analysis.eligible.length === 1
    ) {
      state.pendingTargetKey.engraving = selectedKey;
      setTimeout(() => persistTargetSelection('engraving'), 0);
    }

    const storedText = getStoredProperty(
      item,
      SETTINGS.engraving.propertyName
    );

    if (
      document.activeElement !== input &&
      input.value !== storedText
    ) {
      input.value = storedText;
      updateEngravingCharacterCount();
    }

    state.lastSaved.engraving = storedText;

    if (input.value.trim()) {
      clearEngravingError();
    } else {
      markEngravingEmpty();
    }
  }

  function refreshColorInterface(analysis) {
    const item = analysis.addon;
    const shouldLock = analysis.shouldLock;

    state.existingSelections.color = shouldLock;
    state.items.color = item;
    state.eligibleTargets.color = analysis.eligible;

    const { placeholder, field, notice } = getColorParts();
    if (!placeholder || !field || !notice) return;

    setNoticeContent(notice, SETTINGS.color.existingMessage);
    setAssociatedToggleLocked('color', placeholder, shouldLock);

    if (shouldLock) {
      setAnimatedVisibility(field, false);
      setAnimatedVisibility(
        notice,
        Boolean(state.noticeShown.color)
      );
      state.lastSaved.color = null;
      state.pendingColor = null;
      state.selectedTargetKey.color = '';
      state.pendingTargetKey.color = '';
      clearColorSelection();
      clearColorError();
      clearTargetError('color');
      return;
    }

    state.noticeShown.color = false;
    setAnimatedVisibility(notice, false);

    if (!item) {
      setAnimatedVisibility(field, false);
      state.lastSaved.color = null;
      state.pendingColor = null;
      state.selectedTargetKey.color = '';
      state.pendingTargetKey.color = '';
      clearColorSelection();
      clearColorError();
      clearTargetError('color');
      return;
    }

    setAnimatedVisibility(field, true);

    const assignedTarget = getAddonTargetKey(item, analysis.eligible);
    const selectedKey = renderTargetPicker(
      'color',
      analysis.eligible,
      true,
      assignedTarget
    );

    if (
      selectedKey &&
      !assignedTarget &&
      analysis.eligible.length === 1
    ) {
      state.pendingTargetKey.color = selectedKey;
      setTimeout(() => persistTargetSelection('color'), 0);
    }

    const storedColor = getStoredProperty(
      item,
      SETTINGS.color.propertyName
    );

    if (state.pendingColor) {
      if (getSelectedColor() !== state.pendingColor) {
        selectStoredColor(state.pendingColor);
      }

      if (storedColor === state.pendingColor) {
        state.lastSaved.color = storedColor;
        state.pendingColor = null;
      }
    } else {
      if (getSelectedColor() !== storedColor) {
        selectStoredColor(storedColor);
      }

      state.lastSaved.color = storedColor;
    }

    if (getSelectedColor()) {
      clearColorError();
    } else {
      markColorEmpty();
    }
  }

  async function refreshState() {
    if (state.refreshInProgress) return;
    state.refreshInProgress = true;

    try {
      let cart = await getCart();
      let engravingAnalysis = analyzeCartForKind(cart, 'engraving');
      let colorAnalysis = analyzeCartForKind(cart, 'color');

      if (
        engravingAnalysis.addon &&
        engravingAnalysis.eligible.length === 0
      ) {
        const cleanup = await removeImpossibleAddon(
          cart,
          'engraving',
          engravingAnalysis
        );
        cart = cleanup.cart;
      }

      colorAnalysis = analyzeCartForKind(cart, 'color');

      if (
        colorAnalysis.addon &&
        colorAnalysis.eligible.length === 0
      ) {
        const cleanup = await removeImpossibleAddon(
          cart,
          'color',
          colorAnalysis
        );
        cart = cleanup.cart;
      }

      engravingAnalysis = analyzeCartForKind(cart, 'engraving');
      colorAnalysis = analyzeCartForKind(cart, 'color');

      refreshEngravingInterface(engravingAnalysis);
      refreshColorInterface(colorAnalysis);
    } catch (error) {
      console.error('[Piedra add-ons]', error);
    } finally {
      state.refreshInProgress = false;
    }
  }

  /* -------------------------- CHECKOUT ------------------------- */

  function isCheckoutControl(target) {
    if (!(target instanceof Element)) return false;

    const control = target.closest(
      'button, input[type="submit"], a'
    );
    if (!control) return false;

    if (
      control.matches(
        'button[name="checkout"], ' +
        'input[name="checkout"], ' +
        '[data-checkout-button], ' +
        '[data-testid*="checkout" i]'
      )
    ) {
      return true;
    }

    if (control instanceof HTMLAnchorElement) {
      const href = control.getAttribute('href') || '';
      if (/\/checkout(?:[/?#]|$)/i.test(href)) return true;
    }

    const text = normalize(
      control instanceof HTMLInputElement
        ? control.value
        : control.textContent
    );

    return text === 'checkout' || text.includes('go to checkout');
  }

  async function handleCheckout(event) {
    if (
      state.bypassCheckoutOnce ||
      !isCheckoutControl(event.target)
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const control = event.target.closest(
      'button, input[type="submit"], a'
    );

    const checkoutUrl =
      control instanceof HTMLAnchorElement
        ? control.href
        : `${rootUrl()}checkout`;

    await refreshState();

    let hasValidationError = false;
    let firstInvalidControl = null;

    if (
      state.items.engraving &&
      !state.existingSelections.engraving
    ) {
      const { input } = getEngravingParts();
      const targetSelect = getTargetParts('engraving').targetSelect;
      const value = input?.value.trim() || '';

      if (!state.selectedTargetKey.engraving) {
        markTargetMissing('engraving', { showMessage: true });
        hasValidationError = true;
        firstInvalidControl ||= targetSelect;
      }

      if (!value) {
        markEngravingEmpty(undefined, { showMessage: true });
        hasValidationError = true;
        firstInvalidControl ||= input;
      }
    }

    if (
      state.items.color &&
      !state.existingSelections.color
    ) {
      const selectedColor = getSelectedColor();
      const targetSelect = getTargetParts('color').targetSelect;

      if (!state.selectedTargetKey.color) {
        markTargetMissing('color', { showMessage: true });
        hasValidationError = true;
        firstInvalidControl ||= targetSelect;
      }

      if (!selectedColor) {
        const { radios } = getColorParts();
        markColorEmpty(undefined, { showMessage: true });
        hasValidationError = true;
        firstInvalidControl ||= radios?.[0] || null;
      }
    }

    if (hasValidationError) {
      firstInvalidControl?.focus();
      return;
    }

    if (
      state.items.engraving &&
      !state.existingSelections.engraving
    ) {
      const engravingValue =
        getEngravingParts().input?.value.trim() || '';

      if (
        engravingValue !== state.lastSaved.engraving ||
        state.pendingTargetKey.engraving
      ) {
        const engravingSaved = await saveEngravingText();
        if (!engravingSaved) return;
      }
    }

    if (
      state.items.color &&
      !state.existingSelections.color
    ) {
      const colorValue = getSelectedColor();

      if (
        state.pendingColor ||
        state.pendingTargetKey.color ||
        colorValue !== state.lastSaved.color
      ) {
        const colorSaved = await saveColorSelection(colorValue);
        if (!colorSaved) return;
      }
    }

    state.bypassCheckoutOnce = true;
    window.location.assign(checkoutUrl);
  }
  /* -------------------- CART REQUEST GUARD --------------------- */

  function isCartAddUrl(value) {
    try {
      const url = new URL(String(value), window.location.origin);
      return /\/cart\/add(?:\.js)?$/i.test(url.pathname);
    } catch (_error) {
      return /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(String(value || ''));
    }
  }

  function getRequestUrl(input) {
    if (input instanceof Request) return input.url;
    return String(input || '');
  }

  function parseNumberish(value) {
    const parsed = Number(String(value ?? '').replace(/[^0-9]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function bodyObjectFromFormLike(body) {
    const result = { items: [] };
    const entries = Array.from(body.entries());

    const directId = entries.find(([key]) => key === 'id')?.[1];
    const directQuantity = entries.find(([key]) => key === 'quantity')?.[1];

    if (directId) {
      result.items.push({
        id: parseNumberish(directId),
        quantity: Math.max(1, Number(directQuantity || 1))
      });
    }

    const indexed = new Map();

    entries.forEach(([key, value]) => {
      const match = String(key).match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!match) return;

      const index = Number(match[1]);
      const field = match[2];
      const item = indexed.get(index) || {};
      item[field] = field === 'id' ? parseNumberish(value) : Number(value || 1);
      indexed.set(index, item);
    });

    indexed.forEach((item) => {
      if (item.id) {
        result.items.push({
          id: item.id,
          quantity: Math.max(1, Number(item.quantity || 1))
        });
      }
    });

    return result;
  }

  async function parseCartAddPayload(input, init) {
    let body = init?.body;
    let contentType = '';

    if (input instanceof Request) {
      contentType = input.headers.get('content-type') || '';

      if (body === undefined) {
        try {
          const clone = input.clone();

          if (contentType.includes('application/json')) {
            body = await clone.text();
          } else if (
            contentType.includes('application/x-www-form-urlencoded') ||
            contentType.includes('multipart/form-data')
          ) {
            body = await clone.formData();
          }
        } catch (_error) {
          body = undefined;
        }
      }
    } else if (init?.headers) {
      try {
        const headers = new Headers(init.headers);
        contentType = headers.get('content-type') || '';
      } catch (_error) {
        contentType = '';
      }
    }

    if (!body) return { items: [], bodyType: 'none', rawBody: body };

    if (body instanceof FormData || body instanceof URLSearchParams) {
      return {
        ...bodyObjectFromFormLike(body),
        bodyType: body instanceof FormData ? 'formdata' : 'urlsearchparams',
        rawBody: body
      };
    }

    if (typeof body === 'string') {
      const trimmed = body.trim();

      if (!trimmed) return { items: [], bodyType: 'string', rawBody: body };

      try {
        const parsed = JSON.parse(trimmed);
        const items = Array.isArray(parsed?.items)
          ? parsed.items
          : parsed?.id
            ? [{ id: parsed.id, quantity: parsed.quantity || 1 }]
            : [];

        return {
          items: items.map((item) => ({
            id: parseNumberish(item?.id),
            quantity: Math.max(1, Number(item?.quantity || 1))
          })).filter((item) => item.id),
          bodyType: 'json',
          rawBody: body,
          parsed
        };
      } catch (_error) {
        const params = new URLSearchParams(trimmed);
        return {
          ...bodyObjectFromFormLike(params),
          bodyType: 'urlencoded-string',
          rawBody: body,
          params
        };
      }
    }

    return { items: [], bodyType: 'unknown', rawBody: body };
  }

  function addonKindForVariantId(id) {
    const normalizedId = stringId(parseNumberish(id));

    if (SETTINGS.color.cartAddonVariantIds.includes(normalizedId)) {
      return 'color';
    }

    if (SETTINGS.engraving.cartAddonVariantIds.includes(normalizedId)) {
      return 'engraving';
    }

    return null;
  }

  async function getCurrentBlockedState() {
    try {
      const response = await nativeFetch(`${rootUrl()}cart.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) {
        return { ...state.existingSelections };
      }

      const cart = await response.json();
      return {
        engraving: analyzeCartForKind(cart, 'engraving').shouldLock,
        color: analyzeCartForKind(cart, 'color').shouldLock
      };
    } catch (_error) {
      return { ...state.existingSelections };
    }
  }

  function syntheticBlockedAddResponse() {
    return new Response(JSON.stringify({ items: [] }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function filteredJsonPayload(parsed, blockedKinds) {
    if (!parsed || typeof parsed !== 'object') return parsed;

    if (Array.isArray(parsed.items)) {
      return {
        ...parsed,
        items: parsed.items.filter((item) => {
          const kind = addonKindForVariantId(item?.id);
          return !kind || !blockedKinds.has(kind);
        })
      };
    }

    const directKind = addonKindForVariantId(parsed.id);
    if (directKind && blockedKinds.has(directKind)) {
      return null;
    }

    return parsed;
  }

  async function guardedFetch(input, init = {}) {
    const url = getRequestUrl(input);

    if (!isCartAddUrl(url)) {
      return nativeFetch(input, init);
    }

    const payload = await parseCartAddPayload(input, init);
    const targetedKinds = new Set(
      payload.items
        .map((item) => addonKindForVariantId(item.id))
        .filter(Boolean)
    );

    if (!targetedKinds.size) {
      return nativeFetch(input, init);
    }

    const blockedState = await getCurrentBlockedState();
    const blockedKinds = new Set(
      Array.from(targetedKinds).filter((kind) => blockedState[kind])
    );

    if (!blockedKinds.size) {
      return nativeFetch(input, init);
    }

    blockedKinds.forEach((kind) => {
      state.existingSelections[kind] = true;
      forceToggleOff(
        state.lockedControls[kind]?.control,
        state.lockedControls[kind]?.wrapper
      );
    });

    if (payload.bodyType === 'json') {
      const filtered = filteredJsonPayload(payload.parsed, blockedKinds);

      if (!filtered || (Array.isArray(filtered.items) && !filtered.items.length)) {
        queueRefresh(40);
        return syntheticBlockedAddResponse();
      }

      const nextInit = {
        ...init,
        body: JSON.stringify(filtered)
      };

      queueRefresh(40);
      return nativeFetch(url, nextInit);
    }

    queueRefresh(40);
    return syntheticBlockedAddResponse();
  }

  function installCartRequestGuards() {
    if (!window.__piedraCartFetchGuardInstalled) {
      window.__piedraCartFetchGuardInstalled = true;
      window.fetch = guardedFetch;
    }

    if (
      !window.__piedraCartXhrGuardInstalled &&
      window.XMLHttpRequest?.prototype
    ) {
      window.__piedraCartXhrGuardInstalled = true;

      const originalOpen = window.XMLHttpRequest.prototype.open;
      const originalSend = window.XMLHttpRequest.prototype.send;

      window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this.__piedraMethod = String(method || 'GET').toUpperCase();
        this.__piedraUrl = String(url || '');
        return originalOpen.call(this, method, url, ...rest);
      };

      window.XMLHttpRequest.prototype.send = function(body) {
        if (
          this.__piedraMethod === 'POST' &&
          isCartAddUrl(this.__piedraUrl) &&
          typeof body === 'string'
        ) {
          const targetIds = [
            ...SETTINGS.color.cartAddonVariantIds,
            ...SETTINGS.engraving.cartAddonVariantIds
          ];

          const bodyContainsTarget = targetIds.some((id) =>
            body.includes(String(id))
          );

          if (bodyContainsTarget) {
            const shouldBlockColor =
              state.existingSelections.color &&
              SETTINGS.color.cartAddonVariantIds.some((id) =>
                body.includes(String(id))
              );

            const shouldBlockEngraving =
              state.existingSelections.engraving &&
              SETTINGS.engraving.cartAddonVariantIds.some((id) =>
                body.includes(String(id))
              );

            if (shouldBlockColor || shouldBlockEngraving) {
              this.abort();
              queueRefresh(40);
              return;
            }
          }
        }

        return originalSend.call(this, body);
      };
    }
  }
  /* ------------------------ APP RERENDERS ---------------------- */

  function queueRefresh(delay = 120) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(refreshState, delay);
  }

  function isInsidePiedraInterface(target) {
    return Boolean(
      target instanceof Element &&
      target.closest(
        '[data-piedra-engraving-placeholder], ' +
        '[data-piedra-color-placeholder]'
      )
    );
  }

  function stopLockedToggleInteraction(
    event,
    { showNotice = false } = {}
  ) {
    const blockedKind = getBlockedToggleKind(event.target);
    if (!blockedKind) return false;

    if (
      event.type === 'keydown' &&
      !['Enter', ' ', 'Spacebar'].includes(event.key)
    ) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const record = state.lockedControls[blockedKind];
    forceToggleOff(record?.control, record?.wrapper);

    [0, 20, 80, 200, 600].forEach((delay) => {
      setTimeout(() => {
        const latestRecord = state.lockedControls[blockedKind];
        forceToggleOff(
          latestRecord?.control,
          latestRecord?.wrapper
        );
      }, delay);
    });

    if (showNotice) {
      pulseExistingNotice(blockedKind);
    }

    /*
      The portal shield prevents the app from receiving the interaction.
      This cleanup remains as a final cart-level safeguard in case another
      app changes the cart programmatically.
    */
    queueDuplicateEnforcement(blockedKind);

    return true;
  }

  [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'touchstart',
    'touchend',
    'input',
    'keydown'
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      (event) => {
        stopLockedToggleInteraction(event, {
          showNotice: [
            'pointerdown',
            'mousedown',
            'touchstart',
            'keydown'
          ].includes(eventName)
        });
      },
      true
    );
  });

  document.addEventListener(
    'click',
    (event) => {
      const blockedKind = getBlockedToggleKind(event.target);

      if (blockedKind) {
        stopLockedToggleInteraction(event, { showNotice: true });
        return;
      }

      handleCheckout(event);

      if (!isInsidePiedraInterface(event.target)) {
        queueRefresh(160);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const blockedKind = getBlockedToggleKind(event.target);

      if (blockedKind) {
        stopLockedToggleInteraction(event, { showNotice: true });
        return;
      }

      if (!isInsidePiedraInterface(event.target)) {
        queueRefresh(100);
      }
    },
    true
  );

  document.addEventListener(
    'scroll',
    syncAllPortalShields,
    true
  );

  window.addEventListener(
    'resize',
    syncAllPortalShields,
    { passive: true }
  );

  window.visualViewport?.addEventListener(
    'resize',
    syncAllPortalShields,
    { passive: true }
  );

  window.visualViewport?.addEventListener(
    'scroll',
    syncAllPortalShields,
    { passive: true }
  );

  const observer = new MutationObserver(() => {
    syncAllPortalShields();

    const engravingPlaceholder = getEngravingPlaceholder();
    const colorPlaceholder = getColorPlaceholder();

    if (engravingPlaceholder) {
      buildEngravingInterface(engravingPlaceholder);
    }

    if (colorPlaceholder) {
      buildColorInterface(colorPlaceholder);
    }

    queueRefresh(140);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.__piedraEssentialAddonsRefresh = refreshState;

  installCartRequestGuards();

  buildEngravingInterface(getEngravingPlaceholder());
  buildColorInterface(getColorPlaceholder());
  refreshState();

  setInterval(refreshState, SETTINGS.cartPollInterval);
})();